import {
  Box,
  Card,
  CardActions,
  CardContent,
  Divider,
  Grid,
  Skeleton,
  Table,
  TableFooter,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Fragment, useCallback } from "react";
import SearchInput from "shared/components/SearchInput";
import { useAsyncDispatch } from "shared/helpers/hooks/useAsyncDispatch";
import useTranslation from "shared/helpers/hooks/useTranslation";
import DotsMenu from "shared/ui/DotsMenu";
import TablePaginationActions from "shared/ui/Table/components/TablePAginationActions";
import {
  IAction,
  IFilterOptions,
  rowsPerPageOptions,
} from "shared/ui/Table/constants";
import { ITemplate } from "store/interfaces/campaignDetails";
import { IPaginated } from "store/interfaces/main";
import { setTableLoading } from "store/slicers/common";

type Props = {
  list: IPaginated<ITemplate>;
  actions: any;
  filterOptions: IFilterOptions;
  onChange: () => void;
  isLoading: boolean;
};

const LoadingSkeleton = () => (
  <Box
    sx={{
      display: "flex",
      gap: "18px",
      flexWrap: "wrap",
      paddingTop: 4,
    }}
  >
    {[...Array(8)].map((_, index) => (
      <Skeleton
        key={index}
        variant="rectangular"
        sx={{
          my: 2,
          mx: 1,
          height: "280px",
          width: "21%",
          borderRadius: "20px",
        }}
      />
    ))}
  </Box>
);

const TemplateCardList = ({
  list,
  actions,
  filterOptions,
  onChange,
  isLoading,
}: Props) => {
  const dispatch = useAsyncDispatch();
  const filters = filterOptions?.watch("config");
  const t = useTranslation();

  const handleClickAction = useCallback(
    (action: IAction<ITemplate>, row: ITemplate) => {
      action?.onClick(row);
    },
    []
  );

  const handlePageChange = (_: any, pageNumber: number) => {
    dispatch(setTableLoading(true));
    filterOptions?.reset({
      ...filterOptions.watch(),
      config: {
        ...filters,
        start: pageNumber * filters?.length,
      },
    });
    onChange?.();
    dispatch(setTableLoading(false));
  };

  const handleRowsPerPageChange = (e: any) => {
    const value = e.target.value;
    filterOptions?.reset({
      ...filterOptions.watch(),
      config: {
        ...filters,
        length: value,
      },
    });
    onChange?.();
  };

  return (
    <Fragment>
      <SearchInput filterOptions={filterOptions} fetchData={onChange} />
      {list?.displayData?.length && !isLoading ? (
        <Grid container spacing={2} sx={{ paddingTop: 4 }}>
          {list?.displayData?.map((item: ITemplate) => {
            return (
              <Grid
                item
                xs={6}
                sm={4}
                md={3}
                key={item.id}
                sx={{ width: "100%", position: "relative" }}
              >
                <Card
                  sx={{
                    mb: 2,
                    width: "100%",
                  }}
                  key={item.id}
                >
                  <CardContent>
                    <Typography color={item.questionColor}>
                      {t("question")}
                    </Typography>
                    <Typography color={item.answerColor}>
                      {t("answer")}
                    </Typography>
                    <Typography color={item.buttonTextColor}>
                      {t("button_text")}
                    </Typography>
                    <Box
                      sx={{
                        mt: 1,
                        height: 12,
                        width: 48,
                        backgroundColor: item.buttonColor,
                      }}
                    ></Box>
                  </CardContent>
                  <Divider />
                  <CardActions
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      px: 3,
                    }}
                  >
                    <Typography>{item.name}</Typography>
                    <DotsMenu<ITemplate>
                      actions={actions(item)}
                      onActionClick={handleClickAction}
                      row={item}
                    />
                  </CardActions>
                </Card>
              </Grid>
            );
          })}
          <Table>
            <TableFooter
              sx={{
                "& p": {
                  marginBottom: 0,
                },
              }}
            >
              <TableRow>
                <TablePagination
                  component="td"
                  count={list?.totalRecords}
                  rowsPerPage={filters?.length}
                  rowsPerPageOptions={rowsPerPageOptions}
                  page={filters?.start / filters?.length}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </Grid>
      ) : isLoading ? (
        <LoadingSkeleton />
      ) : (
        <Typography>{t("no_data")}</Typography>
      )}
    </Fragment>
  );
};

export default TemplateCardList;
